// WhatsApp module shell — sidebar entry "WhatsApp". Tabs: Inbox (live),
// Templates / Broadcasts / Flows / Billing & Usage (later phases, shown with
// their phase label), Settings (embeds the existing Meta connection panel).
// Meta Cloud API only: no third-party providers are offered anywhere here.
import { useCallback, useEffect, useState } from "react";
import { NavLink, Navigate, useParams } from "react-router-dom";
import { MessageCircle, Inbox, LayoutTemplate, Megaphone, Workflow, Receipt, Settings, AlertTriangle, Rocket } from "lucide-react";
import { whatsappApi } from "@/services/whatsappApi";
import { useBusiness } from "@/contexts/BusinessContext";
import WhatsAppInbox from "./WhatsAppInbox";
import WhatsAppPlaceholder from "./WhatsAppPlaceholder";
import WhatsAppConnect from "./WhatsAppConnect";
import WhatsAppGetStarted from "./WhatsAppGetStarted";
import WhatsAppIntegration from "@/pages/integrations/WhatsAppIntegration";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: "setup", label: "Get started", icon: Rocket },
  { id: "inbox", label: "Inbox", icon: Inbox },
  { id: "templates", label: "Templates", icon: LayoutTemplate, phase: 2 },
  { id: "broadcasts", label: "Broadcasts", icon: Megaphone, phase: 3 },
  { id: "flows", label: "Flows", icon: Workflow, phase: 4 },
  { id: "billing", label: "Billing & Usage", icon: Receipt, phase: 2 },
  { id: "settings", label: "Settings", icon: Settings },
];

export const isMetaIntegration = (i) =>
  !!i && ["meta_cloud", "api", "cloud_api", "meta"].includes((i.connection_type || "").toLowerCase());

export default function WhatsAppPage() {
  const { tab = "inbox" } = useParams();
  // Selected business from the sidebar switcher (super_admin); the backend
  // clamps everyone else to their own business regardless of this value.
  const { businessId } = useBusiness();
  const [currentUser, setCurrentUser] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [integrationsError, setIntegrationsError] = useState("");
  const [unread, setUnread] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [showLegacyInbox, setShowLegacyInbox] = useState(false);

  const fetchIntegrations = useCallback(async () => {
    try {
      const d = await whatsappApi.integrations({ business_id: businessId || undefined });
      setIntegrations(Array.isArray(d) ? d : d.integrations || []);
      setIntegrationsError("");
    } catch (e) {
      setIntegrations([]);
      setIntegrationsError(e.message || "Could not load WhatsApp numbers");
    } finally {
      setLoaded(true);
    }
  }, [businessId]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)).then(setCurrentUser).catch(() => {});
    fetchIntegrations();
  }, [fetchIntegrations]);

  if (!TABS.some((t) => t.id === tab)) return <Navigate to="/whatsapp" replace />;

  const metaNumbers = integrations.filter(isMetaIntegration);
  const legacyNumbers = integrations.filter((i) => !isMetaIntegration(i));

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="mx-auto max-w-[1400px] space-y-5 p-4 md:p-6" data-testid="whatsapp-page">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#25D366] text-white shadow-sm">
              <MessageCircle className="h-5 w-5" />
            </span>
            <div>
              <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-slate-900">
                WhatsApp
                {unread > 0 && (
                  <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-semibold text-white" data-testid="wa-unread-total">{unread}</span>
                )}
              </h1>
              <p className="text-sm text-slate-500">Inbox, live chat, templates, broadcasts and usage — directly on the Meta WhatsApp Cloud API.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {metaNumbers.map((i) => (
              <span key={i.integration_id} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800">
                <span className={`h-1.5 w-1.5 rounded-full ${i.enabled !== false && i.status !== "disconnected" ? "bg-emerald-500" : "bg-slate-400"}`} />
                {i.display_name || i.phone_number || i.phone_number_id}
              </span>
            ))}
            {legacyNumbers.map((i) => (
              <span key={i.integration_id} title="Connected through a legacy third-party provider — read-only. Reconnect directly with Meta to send." className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800">
                <AlertTriangle className="h-3 w-3" /> {i.display_name || i.sender_number || "Legacy number"} · legacy
              </span>
            ))}
            {loaded && metaNumbers.length === 0 && !integrationsError && (
              <NavLink to="/whatsapp/setup" className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800 hover:border-emerald-300">No number connected — set up free</NavLink>
            )}
          </div>
        </div>

        {integrationsError && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{integrationsError}</span>
          </div>
        )}

        {/* Tabs */}
        <nav className="flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1" data-testid="wa-tabs">
          {TABS.map((t) => (
            <NavLink
              key={t.id}
              to={t.id === "inbox" ? "/whatsapp" : `/whatsapp/${t.id}`}
              end={t.id === "inbox"}
              data-testid={`wa-tab-${t.id}`}
              className={({ isActive }) =>
                `inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                  isActive || (t.id === "inbox" && tab === "inbox") ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <t.icon className="h-4 w-4" />
              {t.label}
              {t.phase && <span className="rounded-full bg-white/20 px-1.5 text-[10px] font-semibold opacity-80 ring-1 ring-inset ring-current/20">P{t.phase}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Body */}
        {tab === "setup" && (
          <WhatsAppGetStarted currentUser={currentUser} businessId={businessId} onConnected={fetchIntegrations} connectedCount={metaNumbers.length} />
        )}
        {tab === "inbox" && (
          loaded && metaNumbers.length === 0 && !showLegacyInbox ? (
            // Nothing connected yet — the inbox would be empty, so lead with the guided setup.
            <WhatsAppGetStarted currentUser={currentUser} businessId={businessId} onConnected={fetchIntegrations}
              hasLegacy={legacyNumbers.length > 0} onViewLegacy={() => setShowLegacyInbox(true)} />
          ) : (
            <WhatsAppInbox key={businessId || "own"} integrations={integrations} currentUser={currentUser} businessId={businessId} onUnreadChange={setUnread} />
          )
        )}
        {tab === "settings" && (
          <div className="space-y-6">
            {metaNumbers.length === 0 ? (
              <WhatsAppGetStarted currentUser={currentUser} businessId={businessId} onConnected={fetchIntegrations} />
            ) : (
              <WhatsAppConnect currentUser={currentUser} businessId={businessId} onConnected={fetchIntegrations} />
            )}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-6">
              <h2 className="mb-4 text-base font-semibold text-slate-900">Connected numbers</h2>
              <WhatsAppIntegration currentUser={currentUser} onRefresh={fetchIntegrations} hideConnect />
            </div>
          </div>
        )}
        {["templates", "broadcasts", "flows", "billing"].includes(tab) && <WhatsAppPlaceholder tab={tab} />}
      </div>
    </div>
  );
}
